import { format, differenceInDays, formatISO, parseISO, isAfter, isBefore } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { isBrowser } from "@helpers/browser";
import { PREORDER_TIMEZONE } from "../constants/Common";
import { Country } from "@constants/Country";
import { fillProductPrices } from "@hooks/usePrices";
import TAG from "@constants/Tag";

// selectedVariantOptions would be an object, something like: { Letter: 'A'}
export function getGalleryImages(product, selectedVariantOptions) {
  if (product?.images) {
    const variant = product.variants.find((v) =>
      v.selectedOptions.every((o) => o.value === selectedVariantOptions[o.name])
    );
    const variantImages = new Set();
    product.variants.forEach((v) => variantImages.add(v.image?.url));
    const images = variant?.image ? [variant.image] : [];
    product.images?.forEach((img) => {
      if (!variantImages.has(img.url)) {
        images.push(img);
      }
    });
    return images;
  }
  return [];
}

export function getShipByDate(date, returnFormat = "MMMM do") {
  const adjustedDate = date.includes("T") ? date.split("T")[0] : date; // Remove the time portion if present
  return format(utcToZonedTime(`${adjustedDate}T23:59:59Z`, PREORDER_TIMEZONE), returnFormat);
}

export function getDaysUntilShipping(shipsByDate) {
  return differenceInDays(
    utcToZonedTime(
      `${formatISO(shipsByDate, { representation: "date" })}T23:59:59Z`,
      PREORDER_TIMEZONE
    ),
    new Date()
  );
}

export function getVariantFromURL(options) {
  if (!isBrowser) return null;

  const urlParams = new URLSearchParams(window.location.search);
  const result = {};

  Object.keys(options).forEach((key) => {
    const value = urlParams.get(key);
    if (value) {
      result[key] = value;
    }
  });

  return Object.keys(result).length ? result : null;
}

export function getVariantOptions(variant) {
  if (!Array.isArray(variant?.selectedOptions)) return {};

  const baseOptions = variant.selectedOptions.reduce(
    (acc, { name, value }) => ({ ...acc, [name]: value }),
    {}
  );

  const urlOptions = getVariantFromURL(baseOptions);
  return urlOptions ? { ...baseOptions, ...urlOptions } : baseOptions;
}

/**
 * Find the variant matching a selected options map (ex: { Letter: "A", Size: "5" }).
 *
 * Matches on the full set of options first, so it stays correct for multi-option
 * products (where the variant title is "A / 5", not "A"), then falls back to the
 * variant title for products whose options are out of sync with the state.
 * Returns undefined when nothing matches, so callers fall back to product level
 * data instead of silently using the wrong variant.
 *
 * @param {Object[]} variants
 * @param {Object} selectedOptions
 * @returns {Object|undefined}
 */
export function findVariantByOptions(variants, selectedOptions) {
  if (!Array.isArray(variants) || variants.length === 0) return undefined;
  if (!selectedOptions || typeof selectedOptions !== "object") return undefined;

  const selectedEntries = Object.entries(selectedOptions).filter(([, value]) => value != null);
  if (selectedEntries.length === 0) return undefined;

  const normalize = (value) => String(value).trim().toLowerCase();

  const variantByOptions = variants.find((variant) => {
    const variantOptions = variant?.selectedOptions;
    if (!Array.isArray(variantOptions) || variantOptions.length === 0) return false;

    return variantOptions.every(({ name, value }) =>
      selectedEntries.some(
        ([selectedName, selectedValue]) =>
          selectedName === name && normalize(selectedValue) === normalize(value)
      )
    );
  });
  if (variantByOptions) return variantByOptions;

  // Fallback: Shopify builds the variant title by joining the option values with " / "
  const titleFromOptions = normalize(selectedEntries.map(([, value]) => value).join(" / "));

  return variants.find(
    (variant) => variant?.title && normalize(variant.title) === titleFromOptions
  );
}

/**
 * Check if sales (discounts) apply to the product based on the product tags.
 * if no sales apply to the product, return true
 * @param {string[]} productTags
 * @param {Object[]} discounts
 * @returns {boolean}
 */

export function isBannedProduct(productTags, discounts) {
  if (!productTags || productTags.length === 0) return false;

  return !discounts.some(({ matchType, matchTags = [] }) => {
    if (matchType === "include") {
      return matchTags.some((tag) => productTags.includes(tag));
    }
    if (matchType === "exclude") {
      return matchTags.every((tag) => !productTags.includes(tag));
    }
    return false;
  });
}

export function resizeShopifyImage(src, size) {
  if (size === null) {
    return src;
  }
  try {
    const match = src.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?$/i);
    if (match) {
      const prefix = src.split(match[0]);
      const suffix = match[0];
      return `${prefix[0]}_${size}${suffix}`;
    }
    return src;
  } catch (error) {
    return src;
  }
}

export function parseQA(str) {
  const pairs = str.split(/;\s*(?=Q:)/);
  return pairs
    .filter((s) => s.trim())
    .map((pair) => {
      const qMatch = pair.match(/Q:\s*(.+?)\s*A:/);
      const aMatch = pair.match(/A:\s*(.+?)(?:;?\s*$)/);
      return {
        question: qMatch?.[1]?.trim() ?? "",
        answer: aMatch?.[1]?.trim() ?? "",
      };
    });
}

function processLLMData(llmData) {
  const result = {};

  result.additionalProperty = [
    {
      "@type": "PropertyValue",
      name: "Material",
      value: llmData.material || "Not specified",
    },
    {
      "@type": "PropertyValue",
      name: "Target Audience",
      value: llmData.target_audience || "Not specified",
    },
    {
      "@type": "PropertyValue",
      name: "Features Summary",
      value: llmData.features_summary || "Not specified",
    },
    {
      "@type": "PropertyValue",
      name: "Benefits Summary",
      value: llmData.benefits_summary || "Not specified",
    },
    {
      "@type": "PropertyValue",
      name: "Intended Use",
      value: llmData.intended_use || "Not specified",
    },
    {
      "@type": "PropertyValue",
      name: "Occasion",
      value: llmData.occasion || "Not specified",
    },
    {
      "@type": "PropertyValue",
      name: "Best With",
      value: llmData.best_with || "Not specified",
    },
  ];
  if (llmData?.review_highlights) {
    result.additionalProperty.push({
      "@type": "PropertyValue",
      name: "Reviews Highlight",
      value: llmData.review_highlights || "Not specified",
    });
  }

  const qaPairs = parseQA(llmData.questions_and_answers || "");
  if (llmData.review_highlights) {
    qaPairs.push({
      question: "What do customers say about this product?",
      answer: llmData.review_highlights,
    });
  }

  if (qaPairs.length) {
    result.subjectOf = {
      "@type": "FAQPage",
      mainEntity: qaPairs.map((pair) => ({
        "@type": "Question",
        name: pair.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: pair.answer,
        },
      })),
    };
  }
  return result;
}

export const buildSEOProductGroupObject = (product, llmData = null) => {
  // Create / Update "vrariesBy" array
  function extractLastPart(input) {
    return input.split(" - ").pop().trim().toLowerCase();
  }
  // Convert Title to slug (ie: Lev Test => lev-test)
  function toSlug(input) {
    return input
      .toLowerCase() // Convert to lowercase
      .trim() // Remove surrounding whitespace
      .replace(/\s+/g, "-") // Replace spaces with dashes
      .replace(/[^a-z0-9-]/g, ""); // Remove invalid characters
  }

  // Build "Alternatives"
  // https://developers.google.com/search/docs/appearance/structured-data/product-variants#page-1:-light-blue-variants
  const variesBy = [];
  let productGroup = {
    "@context": "https://schema.org/",
    "@type": "ProductGroup",
    "@id": toSlug(product.alternatives.title), // missing from CF (to link products together) || frankestein it from handles (not ideal - it might change when adding new products) || find a way to pull the main name from the alternatives

    productGroupID: product.prices?.id?.replace("gid://shopify/Product/", "") || "product_id",
    name: product.title,
    url: `https://www.analuisa.com/products/${product.handle}/`,
    sku: product.variants[0].sku,
    description: (llmData && llmData.description) || product.description,
    image: product.images?.[0]?.url,
    category: product.category,
    brand: {
      "@type": "Brand",
      name: "Ana Luisa",
    },
    shippingDetails: {
      "@type": "OfferShippingDetails",
      "@id": "#shipping_policy",
      shippingRate: {
        "@type": "MonetaryAmount",
        value: 0.0,
        currency: "USD",
      },
      deliveryTime: {
        "@type": "ShippingDeliveryTime",
        handlingTime: {
          "@type": "QuantitativeValue",
          minValue: 0,
          maxValue: 1,
          unitCode: "DAY",
        },
        transitTime: {
          "@type": "QuantitativeValue",
          minValue: 2,
          maxValue: 5,
          unitCode: "DAY",
        },
      },
    },
    audience: {
      "@type": "Audience",
      audienceType: "Women",
    },
  };

  const isFinalSale = Array.isArray(product?.tags) && product.tags.includes("Final Sale");

  const merchantReturnPolicy = isFinalSale
    ? {
        "@type": "MerchantReturnPolicy",
        "@id": "#return_policy",
        applicableCountry: "US",
        returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
      }
    : {
        "@type": "MerchantReturnPolicy",
        "@id": "#return_policy",
        applicableCountry: "US",
        returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
        merchantReturnDays: 60,
        returnMethod: "https://schema.org/ReturnByMail",
        returnFees: "https://schema.org/FreeReturn",
      };
  productGroup.merchantReturnPolicy = merchantReturnPolicy;

  const tags = Array.isArray(product?.tags) ? product.tags : [];

  if (tags.includes("MTO")) {
    const tag = tags.find((t) => typeof t === "string" && t.includes("MTO Timeline:"));
    const match = tag?.match(/(\d+)(?:\s*-\s*(\d+))?/);

    const a = parseInt(match?.[1], 10) || 10;
    const b = parseInt(match?.[2], 10) || a;

    productGroup.shippingDetails ??= {};
    productGroup.shippingDetails.deliveryTime ??= {};

    productGroup.shippingDetails.deliveryTime.handlingTime = {
      "@type": "QuantitativeValue",
      minValue: Math.min(a, b),
      maxValue: Math.max(a, b),
      unitCode: "DAY",
    };
    productGroup.shippingDetails.deliveryTime.transitTime = {
      "@type": "QuantitativeValue",
      minValue: 1,
      maxValue: 3,
      unitCode: "DAY",
    };
  }

  let hasVariant = [];
  if (product?.alternatives.shopifyProducts && product?.alternatives.shopifyProducts.length > 0) {
    hasVariant = product?.alternatives.shopifyProducts.reduce((red, productData) => {
      const contentfulProduct = product?.alternatives.contentfulProducts.find(
        (p) => p.handle === productData.handle
      );
      const shopifyProduct = product?.alternatives.shopifyProducts.find(
        (p) => p.handle === productData.handle
      );

      const prices =
        product?.alternatives.prices.find((p) => (p?.handle || "") === productData.handle) || null;

      const objVariant = {
        "@type": "Product",
        // isVariantOf: productGroup["@id"],
        sku: shopifyProduct.variants[0].sku,
        image: shopifyProduct.images?.[0]?.url,
        name: shopifyProduct.title,
        offers: {
          "@type": "Offer",
          url: `https://www.analuisa.com/products/${shopifyProduct.handle}/`,
          priceCurrency: prices?.currencyCode || "USD",
          price: Number(prices?.finalPrice) || 0,
          itemCondition: "https://schema.org/NewCondition",
          availability: "https://schema.org/InStock",
          shippingDetails: { "@id": "#shipping_policy" },
          hasMerchantReturnPolicy: { "@id": "#return_policy" },
        },
      };

      if (contentfulProduct?.description) {
        objVariant.description = contentfulProduct.description;
      }

      const isSize = contentfulProduct?.attributes.some((a) => a.includes("Size"));
      let size = null;
      const isColor = contentfulProduct?.attributes.some((a) => a.includes("Color"));
      let color = null;
      if (isSize && !variesBy.includes("size")) {
        variesBy.push("size");
      }
      if (isSize) {
        size = extractLastPart(contentfulProduct?.attributes.filter((a) => a.includes("Size"))[0]);
      }
      if (size) objVariant.size = size;

      if (isColor && !variesBy.includes("color")) {
        variesBy.push("color");
      }
      if (isColor) {
        color = extractLastPart(
          contentfulProduct?.attributes.filter((a) => a.includes("Color"))[0]
        );
      }
      if (color) objVariant.color = color;

      red.push(objVariant);
      return red;
    }, []);
  }

  productGroup.variesBy = variesBy;
  productGroup.hasVariant = hasVariant;

  if (llmData) {
    productGroup = {
      ...productGroup,
      ...processLLMData(llmData),
    };
  }
  return productGroup;
};

export const buildSEOProductObject = (product, llmData = null) => {
  // Build SEO JSON-LD
  let productJsonLd = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "@id": `https://www.analuisa.com/products/${product.handle}/`,
    name: `${product.title} | Ana Luisa Jewelry`,
    brand: {
      "@type": "Brand",
      name: "Ana Luisa",
    },
    description: (llmData && llmData.description) || product.description,
    category: product.category,
  };

  // Image
  if (product?.images && product.images.length > 0) {
    productJsonLd.image = product.images[0].url;
  }

  // SKU
  if (product?.variants && product.variants.length > 0) {
    productJsonLd.sku = product.variants[0].sku;
  }

  // OFFER
  let availability = product.variants[0].availableForSale
    ? "https://schema.org/InStock"
    : "https://schema.org/OutOfStock";
  if (product.isPreOrder) {
    availability = "https://schema.org/BackOrder";
  }

  // Product offer
  if (product?.prices?.compareAtPrice) {
    productJsonLd.offers = {
      "@type": "AggregateOffer",
      priceCurrency: product.prices?.currencyCode || "USD",
      lowPrice: Number(product.prices.price),
      highPrice: Number(product.prices.compareAtPrice),
      salePrice: Number(product.prices.price),
      offerCount: "2",
    };
  } else {
    productJsonLd.offers = {
      "@type": "Offer",
      priceCurrency: product?.prices?.currencyCode || "USD",
      price: Number(product?.prices?.price),
    };
  }

  const additionalProperties = [];
  Object.keys(product.productDetails || []).forEach((detail) => {
    // Skip adding null values, e.g. charmDimensions: "null"
    if (product.productDetails[detail] === "null") {
      return;
    }

    let value = product.productDetails[detail];
    // Skip adding null values, e.g. post: ["null"];
    if (!value || value === "null") {
      return;
    }

    if (typeof value === "object") {
      value = value.join(", ");
    }

    additionalProperties.push({
      "@type": "PropertyValue",
      name: detail,
      value,
    });
  });

  productJsonLd.offers = {
    ...productJsonLd.offers,
    url: `https://www.analuisa.com/products/${product.handle}`,
    itemCondition: "https://schema.org/NewCondition",
    availability,
    hasMerchantReturnPolicy: "TRUE",
    additionalProperty: additionalProperties,
    shippingDetails: {
      "@type": "OfferShippingDetails",
      deliveryTime: {
        "@type": "ShippingDeliveryTime",
        businessDays: {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: [],
        },
        transitTime: {
          "@type": "QuantitativeValue",
          minValue: 2,
          maxValue: 5,
          unitCode: "DAY",
        },
      },
    },
  };

  productJsonLd.audience = {
    "@type": "Audience",
    audienceType: "Women",
  };

  if (llmData) {
    productJsonLd = {
      ...productJsonLd,
      ...processLLMData(llmData),
    };
  }

  return productJsonLd;
};

export const isVariantOutOfStock = (variant, countryCode = null) =>
  variant?.availableForSale === false ||
  (variant?.inventoryPolicy === "CONTINUE" && Country.US !== countryCode); // Pre-order capabilities disabled outside of US;

export const isOutOfStock = (product, countryCode = null) => {
  const { variants } = product;
  return variants.every((variant) => isVariantOutOfStock(variant, countryCode));
};

// Convert Shopify Object ID
// ex: "gid://shopify/ProductVariant/64982369?cartId=52046502" to "64982369"
export function getShopifyObjectId(id) {
  return id?.split("/")?.pop()?.split("?")?.[0] || id;
}

/**
 * This function produces an object with the same structure as
 * what's created in the old code with setProduct.
 *
 * Locale is taken into account too.
 *
 * {
 *    node
 *    contentful
 * }
 *
 */
export function populateProduct(data, locale = "en-US") {
  // contentful field
  const contentfulEdges = data?.allContentfulProductPage?.edges;

  let localeContentful;
  if (contentfulEdges && contentfulEdges.length > 0) {
    localeContentful = contentfulEdges[0].node;
    for (const e of contentfulEdges) {
      if (e.node.node_locale === locale) {
        localeContentful = e.node;
        break;
      }
    }
  }
  // shopify field
  const shopify = data.shopifyProduct;
  return {
    node: shopify,
    contentful: localeContentful,
  };
}

export function fillPrices(products, priceInfoList) {
  return products?.map((p) => {
    const priceInfo = priceInfoList?.find((pi) => pi.handle === p.node.handle);
    return { ...p, node: fillProductPrices(p.node, priceInfo) };
  });
}

export function mapShopifyProductsByHandle(shopifyProducts) {
  return shopifyProducts.reduce((acc, product) => {
    acc[product.handle] = product;
    return acc;
  }, {});
}

export function mapContentfulProductsByHandle(contentfulProducts) {
  return contentfulProducts.reduce((acc, product) => {
    acc[product.node.handle] = product;
    return acc;
  }, {});
}

export function populateBaseProducts(data, pageContext) {
  const shopifyProductsByHandle = mapShopifyProductsByHandle(data.allShopifyProduct.nodes);
  const contentfulProductsByHandle = mapContentfulProductsByHandle(
    data.allContentfulProductPage.edges
  );

  return pageContext.collectionProductHandles
    .map((handle) => {
      const shopifyProduct = shopifyProductsByHandle[handle];
      const isHeadlessPublished = !!shopifyProduct?.publications?.some(
        (p) =>
          p?.publication?.name ===
          (process.env.GATSBY_SHOPIFY1_STORE_NAME === "analuisa-staging"
            ? "Analuisa Staging Headless"
            : "Ana Luisa Headless")
      );
      if (!isHeadlessPublished) return;

      const contentfulProduct = contentfulProductsByHandle[handle];
      if (!shopifyProduct || !contentfulProduct) {
        return;
      }

      contentfulProduct.node.productAlternatives = {
        alternatives: (contentfulProduct.node.productAlternatives?.alternatives || []).map(
          (alternativeData) => ({
            ...alternativeData,
            node: shopifyProductsByHandle[alternativeData.handle],
            contentful: contentfulProductsByHandle[alternativeData.handle],
          })
        ),
      };

      return { node: shopifyProduct, contentful: contentfulProduct };
    })
    .filter((p) => p);
}

export function reorderSolidGold(collectionProducts, productType) {
  const { productTypeProducts, others } = collectionProducts.reduce(
    (acc, product) => {
      if (product.contentful?.node?.type?.[0] === productType) {
        acc.productTypeProducts.push(product);
      } else {
        acc.others.push(product);
      }
      return acc;
    },
    {
      productTypeProducts: [],
      others: [],
    }
  );
  return [...productTypeProducts, ...others];
}

export function getProductPricesForIntelligems(product) {
  return {
    final_price: product?.final_price,
    full_price: product?.full_price,
    compareAtPrice: product?.compareAtPrice,
    discount_given: product?.discount_given,
    formattedDiscount: product?.formattedDiscount,
    discountAmount: product?.discountAmount,
  };
}

export const isBetweenDates = (startDate, endDate) => {
  if (!startDate || !endDate) return false;

  const start = utcToZonedTime(startDate, PREORDER_TIMEZONE);
  const end = utcToZonedTime(`${endDate}T23:59:59Z`, PREORDER_TIMEZONE);
  const now = new Date();
  return isAfter(now, start) && isBefore(now, end);
};

/**
 * Contentful variant data (sku, isPreOrder, preOrderStartDate, preOrderEndDate, preOrderShippingDate).
 * Gatsby products expose it at the root, products from get-product under `contentful`.
 */
function getProductVariantsData(product) {
  return (
    product?.productVariants ||
    product?.contentful?.productVariants ||
    product?.contentful?.node?.productVariants ||
    []
  );
}

/**
 * @param {Object} product The product holding the Contentful variant data.
 * @param {Object} variant The Shopify variant (matched by sku).
 * @returns {Object|null} The Contentful variant data, or null if not found.
 */
export function getVariantPreOrderData(product, variant) {
  if (!variant?.sku) return null;
  return getProductVariantsData(product).find((v) => v?.sku === variant.sku) || null;
}

const isActivePreOrder = (variantData) =>
  variantData?.isPreOrder === true &&
  isBetweenDates(variantData.preOrderStartDate, variantData.preOrderEndDate);

const formatPreOrderShipDate = (shippingDate) => {
  if (!shippingDate) return null;
  try {
    return format(parseISO(shippingDate), "MMM d, yyyy");
  } catch (error) {
    console.error(error);
    return null;
  }
};

/**
 * Bundle components follow the variant selected on the bundle (e.g. ring size), default to the first one.
 */
function getBundleComponentVariant(bundleProduct, selectedVariant) {
  const variants =
    bundleProduct?.node?.variants ||
    bundleProduct?.shopify?.variants ||
    bundleProduct?.variants ||
    [];
  return variants.find((v) => v?.title === selectedVariant?.title) || variants[0];
}

function isVariantPreOrder(product, variant) {
  return (
    variant?.inventoryPolicy === "CONTINUE" &&
    isActivePreOrder(getVariantPreOrderData(product, variant))
  );
}

/**
 * Whether the selected variant (or, for bundles, any bundle component) is currently on pre-order.
 */
export function isPreorder(product, selectedVariant) {
  const isBundle = product?.bundleProducts?.length > 0;
  if (isBundle) {
    return product.bundleProducts.some((p) =>
      isVariantPreOrder(p, getBundleComponentVariant(p, selectedVariant))
    );
  }
  return isVariantPreOrder(product, selectedVariant);
}

/**
 * Product-level pre-order, for places with no selected variant (cards, tags, listings):
 * true only when every variant is currently on pre-order.
 */
export function isProductPreOrder(product) {
  const variantsData = getProductVariantsData(product);
  return variantsData.length > 0 && variantsData.every(isActivePreOrder);
}

/**
 * Latest pre-order shipping date across the product variants (raw yyyy-MM-dd), for listings.
 */
export function getProductPreOrderShippingDate(product) {
  const dates = getProductVariantsData(product)
    .filter((v) => v?.isPreOrder === true && v.preOrderShippingDate)
    .map((v) => v.preOrderShippingDate)
    .sort();
  return dates.length > 0 ? dates[dates.length - 1] : null;
}

/**
 * Gets the pre-order shipping date for the selected variant of a product or bundle
 * Bundle logic: If the product is a bundle, the function will return the furthest pre-order shipping date within the bundle components.
 * @param {Object} product The product object to get the pre-order shipping date for.
 * @param {Object} selectedVariant The selected Shopify variant.
 * @returns {string|null} The formatted pre-order shipping date, or null if not available.
 */
export function getPreOrderShipDate(product, selectedVariant) {
  const isBundle = product?.bundleProducts?.length > 0;
  if (isBundle) {
    const dates = product.bundleProducts
      .map((p) => getVariantPreOrderData(p, getBundleComponentVariant(p, selectedVariant)))
      .filter((v) => v?.isPreOrder === true && v.preOrderShippingDate)
      .map((v) => v.preOrderShippingDate)
      .sort();
    return formatPreOrderShipDate(dates[dates.length - 1]);
  }
  const variantData = getVariantPreOrderData(product, selectedVariant);
  return variantData?.isPreOrder === true
    ? formatPreOrderShipDate(variantData.preOrderShippingDate)
    : null;
}

/**
 * Converts the products of a bundle into the normalized product objects used to create cart line items
 *
 * Prices are looked up in `igPrices` by the product's Shopify product ID.
 * Preorder and made-to-order information is also normalized so it can be
 * passed to the cart, and final-sale status is derived from the product's flags or tags.
 *
 * Non-bundle products return an empty array.
 *
 * @param {Object} product Product that may contain a `bundleProducts` array.
 * @param {Object} igPrices Intelligems prices keyed by Shopify product ID.
 * @param {Object} selectedVariant Variant selected for the parent bundle.
 * @returns {Object[]} Normalized bundle components, or an empty array.
 */

function extractBundleProducts(product, igPrices, selectedVariant) {
  let products = [];
  if (product.bundleProducts?.length > 0) {
    products = product.bundleProducts.map((bundleProduct) => {
      const shopifyProduct = bundleProduct?.node || bundleProduct?.shopify || {};
      const contentfulProduct = bundleProduct?.contentful?.node || bundleProduct?.contentful || {};
      const variant =
        bundleProduct.variants?.find((x) => x.title === selectedVariant?.title) ||
        bundleProduct.variants?.[0];
      const preOrderVariant = variant || getBundleComponentVariant(bundleProduct, selectedVariant);
      const isPreOrder = isVariantPreOrder(bundleProduct, preOrderVariant);

      const madeToOrder = bundleProduct?.madeToOrder || contentfulProduct?.madeToOrder;
      const shippingDate = isPreOrder ? getPreOrderShipDate(bundleProduct, preOrderVariant) : null;
      const mtoTimeline =
        madeToOrder?.madeToOrderTimelines && madeToOrder?.madeToOrderTimelines !== "null"
          ? madeToOrder?.madeToOrderTimelines
          : null;
      const productId = shopifyProduct?.id || shopifyProduct?.shopifyId;
      const tags = shopifyProduct?.tags || bundleProduct?.tags || [];

      return {
        title: bundleProduct.title || contentfulProduct?.title,
        productId,
        productHandle: bundleProduct.handle,
        sku: variant?.sku,
        category: bundleProduct.productType || shopifyProduct?.productType,
        images: bundleProduct.images || shopifyProduct?.images,
        variant,
        quantity: bundleProduct.quantity || 1,
        prices: igPrices[productId],
        isPreOrder,
        preOrderShipDate: shippingDate,
        isFinalSale:
          bundleProduct.isFinalSale ||
          tags?.includes(TAG.FINAL_SALE) ||
          contentfulProduct?.tags?.includes(TAG.FINAL_SALE),
        mtoTimeline,
      };
    });
  }
  return products;
}

/**
 * Builds the cart payload for each product being added
 */
function buildCartLineItems(
  productsToAdd,
  selectedVariant,
  {
    countryCode,
    currencyCode,
    discountsInfo,
    trackingProperties = null,
    bundleName = null,
    isByob = false,
    collectionHandle = null,
  } = {}
) {
  return productsToAdd.map((productToAdd) => {
    const variant = productToAdd.variant || selectedVariant;
    const dataProductToAdd = {
      quantity: productToAdd.quantity || 1,
      attributes: [],
      variant: {
        id: variant?.id || variant?.shopifyId,
        sku: variant?.sku,
        price:
          productToAdd.prices?.finalPrice != null ? Number(productToAdd.prices.finalPrice) : null,
        compareAtPrice: productToAdd.prices?.compareAtPrice
          ? Number(productToAdd.prices.compareAtPrice)
          : null,
        product: {
          title: productToAdd.title,
          id: productToAdd.productId ?? productToAdd.id,
          handle: productToAdd.productHandle ?? productToAdd.handle,
          productType: productToAdd.category,
          images: productToAdd.images,
        },
      },
      attributesProperties: {
        isPreOrder: productToAdd.isPreOrder,
        mtoTimeline: productToAdd.mtoTimeline,
        mtoType: productToAdd.mtoType,
        preOrderShipDate: productToAdd.preOrderShipDate,
        isUS: countryCode === Country.US,
        currencyCode,
        prices: productToAdd.prices,
        igPrices: productToAdd.prices,
        isFinalSale:
          productToAdd.isFinalSale ??
          (productToAdd.tags?.includes(TAG.FINAL_SALE) ||
            productToAdd.tags?.includes(TAG.CUSTOMIZABLE)),
        discountsInfo,
        mtoSettings: productToAdd.mtoSettings,
        bundleName,
        isByob,
        collectionHandle,
      },
    };

    if (trackingProperties) {
      dataProductToAdd.trackingProperties = trackingProperties;
    }

    return dataProductToAdd;
  });
}

/**
 * Prepares products and cart line items for add-to-cart request.
 */
export function prepareAddToCart({
  product,
  selectedVariant,
  productPrices,
  bundlePrices = {},
  additionalProducts = [],
  productOverrides = {},
  countryCode,
  currencyCode,
  discountsInfo,
  trackingProperties = null,
  isByob,
  collectionHandle,
}) {
  const variant =
    productOverrides.variant || selectedVariant || product.variant || product.variants?.[0];

  const mainProduct = {
    ...product,
    ...productOverrides,
    productId: productOverrides.productId ?? product.productId ?? product.shopifyId ?? product.id,
    productHandle: productOverrides.productHandle ?? product.productHandle ?? product.handle,
    category: productOverrides.category ?? product.category ?? product.productType,
    variant,
    sku: productOverrides.sku ?? product.sku ?? variant?.sku,
    prices: productPrices ?? productOverrides.prices ?? product.prices ?? product,
  };
  const bundleProducts = extractBundleProducts(product, bundlePrices, selectedVariant);
  const productsToAdd = bundleProducts.length > 0 ? bundleProducts : [mainProduct];
  const dataProductsToAdd = buildCartLineItems(productsToAdd, variant, {
    countryCode,
    currencyCode,
    discountsInfo,
    trackingProperties,
    bundleName: bundleProducts.length > 0 ? product.title : null,
    isByob,
    collectionHandle,
  });
  let additionalProductsToAdd = [];
  if (additionalProducts.length > 0) {
    additionalProductsToAdd = buildCartLineItems(additionalProducts, null, {
      countryCode,
      currencyCode,
      discountsInfo,
    });
  }

  return {
    isBundle: bundleProducts.length > 0,
    productsToAdd: [...productsToAdd, ...additionalProducts],
    dataProductsToAdd: [...dataProductsToAdd, ...additionalProductsToAdd],
  };
}
